<script setup lang="ts">
import type { PdpEcommerceBlockProductNameRedesignProps } from './PdpEcommerceBlockProductNameRedesign.props'
const props = defineProps<PdpEcommerceBlockProductNameRedesignProps>()
const MAX_LABEL_NUMBER = 3

const items = computed(() => {
  return props.tags?.slice(0, MAX_LABEL_NUMBER) ?? []
})

const emit = defineEmits<{
  'update-wishlist': []
  'tag-click': [tag: string]
}>()

const { formatPriceByLocale } = useFormatPrice()
const { currentCurrency } = useCurrency()
const { isShoppable: isWebsiteShoppable } = useIsShoppable()
// The prop is false for non-shoppable products (e.g. Armani Casa infocommerce)
const isShoppable = computed(
  () => props.isShoppable && isWebsiteShoppable.value
)
</script>

<template>
  <div>
    <div
      v-if="items.length > 0 || (isLastInStock && !hideDetails)"
      class="gap-xs lg:mb-xs mb-[18px] flex"
    >
      <div v-if="items.length > 0" class="gap-xs flex">
        <AtomsBadgeProductCart
          v-for="(tag, index) in items"
          :key="'pdp-badge-product-card-' + index"
          :text="tag"
          is-interactive
          underline-effect="appear"
          @click="emit('tag-click', tag)"
        />
      </div>

      <AtomsBadgeProductCart
        v-if="isLastInStock && !hideDetails"
        :text="$ts('pdp.productDetails.informationSection.lastInStock')"
        class="shrink-0"
      />
    </div>

    <div
      class="gap-xxs lg:gap-sm text-primitives-black flex flex-col justify-start lg:h-auto"
    >
      <div class="flex items-center justify-between gap-3 lg:gap-6 xl:gap-3">
        <component
          :is="isHeading ? 'h1' : 'span'"
          class="text-book-6 lg:text-book-5 uppercase"
        >
          {{ title }}
        </component>

        <PdpEcommerceBlockProductActions
          class="hidden lg:flex"
          :product-code="productCode"
          :has-wishlist="isShoppable"
          @update-wishlist="emit('update-wishlist')"
        />
      </div>

      <div v-if="isShoppable" class="text-light-6 order-2 lg:order-3">
        <div class="flex gap-x-2">
          <span
            v-if="oldPrice"
            class="text-primitives-grey-200 text-light-strike-6 line-through"
          >
            {{ formatPriceByLocale(currentCurrency, oldPrice) }}
          </span>
          <span v-if="price" class="text-light-6">
            {{ formatPriceByLocale(currentCurrency, price) }}
          </span>
          <span v-if="discount" class="text-medium-6">{{ discount }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
:root {
  --product-name-height: 82px;
}
</style>
